// src/admin/components/BannerManagement.tsx
import React, { useEffect, useState } from 'react';
import {
  FaPlus,
  FaTimes,
  FaTrashAlt,
  FaCloudUploadAlt,
} from 'react-icons/fa';

interface AdBanner {
  id: number;
  imageUrl: string;
  linkUrl: string;
}

const BannerManagement: React.FC = () => {
  const [banners, setBanners] = useState<AdBanner[]>([]);
  const [newBanner, setNewBanner] = useState<{
    imageFile: File | null;
    linkUrl: string;
  }>({
    imageFile: null,
    linkUrl: '',
  });

  const [preview, setPreview] = useState<string | null>(null);
  const [isAdding, setIsAdding] = useState(false);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const API_BASE_URL = 'http://localhost:2026';

  const fetchBanners = async () => {
    setIsLoading(true);
    try {
      const res = await fetch(`${API_BASE_URL}/api/banners`);
      const data = await res.json();
      setBanners(data);
    } catch {
      setError('Failed to load banners');
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    fetchBanners();
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0] || null;
    setNewBanner({ ...newBanner, imageFile: file });

    if (file) {
      setPreview(URL.createObjectURL(file));
    } else {
      setPreview(null);
    }
  };

  const handleAddBanner = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!newBanner.imageFile) return;

    setIsLoading(true);
    const formData = new FormData();
    formData.append('image', newBanner.imageFile);
    formData.append('linkUrl', newBanner.linkUrl);

    try {
      await fetch(`${API_BASE_URL}/api/banners`, {
        method: 'POST',
        body: formData,
      });
      fetchBanners();
      setIsAdding(false);
      setNewBanner({ imageFile: null, linkUrl: '' });
      setPreview(null);
    } catch {
      setError('Failed to add banner');
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveBanner = async (id: number) => {
    if (!window.confirm('Delete this banner?')) return;
    await fetch(`${API_BASE_URL}/api/banners/${id}`, { method: 'DELETE' });
    fetchBanners();
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-indigo-50 to-purple-50 px-6 py-8">
      <div className="max-w-7xl mx-auto">

        {/* Header */}
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="text-3xl font-bold text-gray-800">
              Banner Management
            </h1>
            <p className="text-sm text-gray-500">
              Manage homepage banners
            </p>
          </div>

          <button
            onClick={() => setIsAdding(!isAdding)}
            className={`px-6 py-3 rounded-xl text-white font-semibold
            shadow-md flex items-center gap-2 transition
            ${isAdding
              ? 'bg-red-500 hover:bg-red-600'
              : 'bg-teal-700 hover:bg-teal-600'
            }`}
          >
            {isAdding ? <FaTimes /> : <FaPlus />}
            {isAdding ? 'Cancel' : 'Add Banner'}
          </button>
        </div>

        {/* Add Banner */}
        {isAdding && (
          <div className="bg-white rounded-2xl shadow-xl border p-6 mb-10">
            <h2 className="text-xl font-semibold mb-6">
              Add New Banner
            </h2>

            <form onSubmit={handleAddBanner} className="space-y-8">

              {/* Upload + Preview */}
              <div>
                <label className="text-sm font-medium text-gray-700 mb-2 block">
                  Banner Image
                </label>

                <div className="grid grid-cols-1 md:grid-cols-2 gap-6">

                  {/* Upload Box */}
                  <div>
                    <input
                      type="file"
                      id="bannerUpload"
                      accept="image/*"
                      onChange={handleFileChange}
                      className="hidden"
                    />

                    <label
                      htmlFor="bannerUpload"
                      className="cursor-pointer flex flex-col items-center justify-center
                      border-2 border-dashed border-teal-400
                      rounded-2xl h-56
                      bg-teal-50 hover:bg-teal-100 transition"
                    >
                      <FaCloudUploadAlt
                        size={42}
                        className="text-teal-600 mb-2"
                      />
                      <p className="font-semibold text-gray-700">
                        Click here to upload
                      </p>
                      <p className="text-xs text-gray-500 mt-1">
                        PNG, JPG, JPEG supported
                      </p>
                    </label>
                  </div>

                  {/* Auto Preview */}
                  {preview && (
                    <div className="h-56 w-full border rounded-2xl
                    bg-gray-100 overflow-hidden shadow">
                      <img
                        src={preview}
                        className="h-full w-full object-contain"
                      />
                    </div>
                  )}

                </div>
              </div>

              {/* Link URL (Half Width) */}
              <div className="md:w-1/2">
                <label className="text-sm font-medium text-gray-700">
                  Link URL
                </label>
                <input
                  type="text"
                  value={newBanner.linkUrl}
                  onChange={(e) =>
                    setNewBanner({ ...newBanner, linkUrl: e.target.value })
                  }
                  placeholder="https://example.com"
                  className="mt-1 w-full p-3 rounded-xl border
                  focus:ring-2 focus:ring-teal-400 outline-none"
                  required
                />
              </div>

              <button
                type="submit"
                disabled={isLoading}
                className="w-full py-3 rounded-2xl
                bg-teal-700 hover:bg-teal-600
                text-white font-bold transition"
              >
                {isLoading ? 'Saving...' : 'Save Banner'}
              </button>
            </form>
          </div>
        )}

        {/* Existing Banners */}
        <div className="bg-white rounded-2xl shadow-md p-6">
          <h2 className="text-xl font-semibold mb-6">
            Existing Banners
          </h2>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {banners.map((banner) => (
              <div
                key={banner.id}
                className="relative bg-white rounded-2xl
                shadow hover:shadow-xl transition overflow-hidden"
              >
                <img
                  src={`${API_BASE_URL}${banner.imageUrl}`}
                  className="h-48 w-full object-cover"
                />

                <button
                  onClick={() => handleRemoveBanner(banner.id)}
                  className="absolute top-3 right-3
                  bg-red-500 hover:bg-red-600
                  p-2 rounded-full text-white"
                >
                  <FaTrashAlt size={14} />
                </button>
              </div>
            ))}
          </div>

          {banners.length === 0 && (
            <p className="text-center text-gray-500 mt-6">
              No banners found
            </p>
          )}
        </div>

        {error && (
          <p className="text-red-500 text-center mt-6">
            {error}
          </p>
        )}
      </div>
    </div>
  );
};

export default BannerManagement;
